import { z } from 'zod';
import { RoleSchema, CapabilitySchema } from '../roles.js';
import { UUIDSchema } from '../ids.js';
import { CredentialPolicy } from './credential.js';
export const LoginBody = z.object({
    email: z.string().email().max(254),
    password: z.string().min(1).max(1024),
    otp: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
});
export const RefreshBody = z.object({
    refresh_token: z.string().min(1).max(1024).optional(),
});
export const ForgotBody = z.object({
    email: z.string().email().max(254),
});
export const ResetBody = z.object({
    token: z.string().min(1).max(2048),
    new_password: z.string().min(12).max(1024),
});
export const PublicUser = z.object({
    id: UUIDSchema,
    email: z.string().email(),
    name: z.string(),
    role: RoleSchema,
    tenant_id: UUIDSchema.nullable(),
    mfa_enrolled: z.boolean(),
});
export const PublicTenant = z.object({
    id: UUIDSchema,
    name: z.string(),
    status: z.enum(['active', 'suspended', 'archived']),
});
export const SiteModeSchema = z.enum(['commercial_multi_tenant', 'single_tenant', 'multi_family']);
export const MeResponse = z.object({
    user: PublicUser,
    tenant: PublicTenant.nullable(),
    capabilities: z.array(CapabilitySchema),
    site_mode: SiteModeSchema,
    /**
     * Auto-derived for single_tenant/multi_family installs (the one and only
     * active tenant). Null in commercial_multi_tenant mode — master_admin
     * must pick one explicitly via the scope chip. Also null when
     * `default_tenant_health` is anything other than `ok` / `unset`.
     */
    default_tenant_id: UUIDSchema.nullable(),
    /**
     * Operator-visible health of the pinned `site.default_tenant_id`:
     *   - `ok`       : pinned tenant exists and is active
     *   - `missing`  : setting points at a tenant that no longer exists
     *   - `inactive` : pinned tenant is suspended/archived
     *   - `unset`    : no pinned setting (legacy install — first-active
     *                  fallback was used). Always `ok` in commercial mode.
     */
    default_tenant_health: z.enum(['ok', 'missing', 'inactive', 'unset']).default('ok'),
    credential_policy: CredentialPolicy,
});
// refresh_token is intentionally NOT in the LoginResponse — it's delivered
// only as an HttpOnly cookie. Keeping it out of the JSON body prevents
// JavaScript code (and any XSS) from exfiltrating refresh credentials.
export const LoginSuccessResponse = z.object({
    access_token: z.string(),
    user: PublicUser,
});
/**
 * Returned when a privileged role's password validated but they haven't
 * yet enrolled MFA. The client uses the bundled `mfa_setup_token` with
 * passkey setup by default, or `/auth/mfa/setup/start` and
 * `/auth/mfa/setup/verify` as the authenticator fallback, then logs in
 * again normally.
 */
export const LoginMfaSetupRequiredResponse = z.object({
    mfa_setup_required: z.literal(true),
    mfa_setup_token: z.string(),
    expires_in_sec: z.number().int(),
    message: z.string().optional(),
});
export const LoginMfaChallengeRequiredResponse = z.object({
    mfa_required: z.literal(true),
    mfa_challenge_token: z.string(),
    expires_in_sec: z.number().int(),
    passkey_available: z.boolean().optional(),
    totp_available: z.boolean().optional(),
});
export const LoginResponse = z.union([
    LoginSuccessResponse,
    LoginMfaSetupRequiredResponse,
    LoginMfaChallengeRequiredResponse,
]);
export function isMfaSetupRequired(r) {
    return r.mfa_setup_required === true;
}
export function isMfaChallengeRequired(r) {
    return r.mfa_required === true;
}
// ---------- /users CRUD ----------
//
// Portal-user lifecycle managed by master admins (any tenant) and
// tenant admins (their own tenant). master-only operations are gated
// by the role check inside the route, not just by capability.
export const UserStatusSchema = z.enum(['active', 'invited', 'suspended']);
export const ListUsersQuery = z.object({
    status: UserStatusSchema.optional(),
    role: RoleSchema.optional(),
    q: z.string().min(1).max(120).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const CreateUserBody = z.object({
    email: z.string().email().max(254),
    name: z.string().min(1).max(120),
    role: RoleSchema,
    initial_password: z.string().min(12).max(1024).optional(),
});
/**
 * Master-only `/admin/users` accepts an optional tenant_id. Validated via
 * Zod so a typo / non-UUID body field is caught as a 400 instead of
 * sneaking through to Prisma as a 500.
 */
export const AdminCreateUserBody = CreateUserBody.extend({
    tenant_id: UUIDSchema.optional(),
});
export const UpdateUserBody = z.object({
    name: z.string().min(1).max(120).optional(),
    role: RoleSchema.optional(),
    status: UserStatusSchema.optional(),
});
/**
 * /admin/users PATCH accepts an optional tenant_id reassignment so the
 * master can promote/demote across the master/tenant boundary. Use null
 * to clear (set when promoting to master_admin).
 */
export const AdminUpdateUserBody = UpdateUserBody.extend({
    tenant_id: UUIDSchema.nullable().optional(),
});
export const AdminPasswordResetBody = z.object({
    // Audited operator-set temporary passwords. Self-service reset keeps the
    // stricter ResetBody 12-character floor.
    new_password: z.string().min(8).max(1024),
});
export const UserInviteResponse = z.object({
    user: PublicUser,
    invite_token: z.string(),
});
// ---------- /employees/bulk ----------
export const BulkEmployeeRow = z.object({
    email: z.string().email().max(254),
    name: z.string().min(1).max(120),
    role: z.enum(['employee', 'manager']).default('employee'),
    schedule_id: UUIDSchema.optional(),
});
export const BulkEmployeesBody = z.object({
    /** When true, the route validates + reports issues without writing. */
    dry_run: z.boolean().default(false),
    rows: z.array(BulkEmployeeRow).min(1).max(500),
});
export const BulkEmployeeError = z.object({
    index: z.number().int(),
    email: z.string(),
    code: z.enum([
        'duplicate_in_batch',
        'duplicate_existing',
        'schedule_required',
        'schedule_not_in_tenant',
        'validation_error',
    ]),
    message: z.string(),
});
export const BulkEmployeesResponse = z.object({
    dry_run: z.boolean(),
    total: z.number().int(),
    valid: z.number().int(),
    errors: z.array(BulkEmployeeError),
    created: z.array(z.object({ id: UUIDSchema, email: z.string() })),
});
//# sourceMappingURL=auth.js.map